<template>
   <!-- 👤 PREMIUM WEB CUSTOMER & PARTNER DASHBOARD PORTAL -->
   <div v-if="authUser" class="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <!-- Welcome Header -->
      <div class="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-100 pb-5">
         <div>
            <h1 class="text-3xl font-extrabold tracking-tight text-gray-900 font-bold">My Profile</h1>
            <p v-if="authUser.user.type === 'web'" class="mt-1.5 text-sm text-gray-500 font-regular">Manage your profile details, update your security parameters, and review your transfer reservations.</p>
            <p v-else class="mt-1.5 text-sm text-gray-500 font-regular">Manage your partner profile details, update your company info, and review your security parameters.</p>
         </div>
      </div>

      <!-- Main Layout Grid -->
      <div class="grid grid-cols-1 gap-8 lg:grid-cols-4">
         <!-- Left Sidebar: Profile card and Navigation Tabs -->
         <div class="lg:col-span-1 flex flex-col gap-6">
            <!-- User Summary Card -->
            <div class="bg-white rounded-2xl shadow-custom-symetric overflow-hidden border border-gray-100 p-6 flex flex-col items-center text-center transition-all duration-300 hover:shadow-lg">
               <div class="relative group cursor-pointer" @click="handleChooseAvatar">
                  <img v-if="userAccount.personal.photo" class="h-24 w-24 rounded-full object-cover ring-4 ring-theme-active/20 group-hover:ring-theme-active/50 transition-all duration-300" :src="userAccount.personal.photo" alt="">
                  <img v-else-if="userAccount.personal.photoName != ''" class="h-24 w-24 rounded-full object-cover ring-4 ring-theme-active/20 group-hover:ring-theme-active/50 transition-all duration-300" :src="userAccount.personal.photoName" alt="">
                  <img v-else class="h-24 w-24 rounded-full object-cover ring-4 ring-theme-active/20 group-hover:ring-theme-active/50 transition-all duration-300" src="/assets/avatars/default.jpg" alt="">
                  
                  <div class="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                     <font-awesome-icon icon="fa-solid fa-camera" class="text-white text-lg" />
                  </div>
               </div>
               <h3 class="mt-4 font-bold text-lg text-gray-900">{{ userAccount.personal.fullName || 'User' }}</h3>
               <p class="text-sm text-gray-500 font-regular truncate max-w-full">{{ userAccount.personal.email }}</p>
               <span v-if="authUser.user.type === 'web'" class="mt-3 inline-flex items-center rounded-full bg-theme-active/10 px-3 py-1 text-xs font-semibold text-theme-active uppercase tracking-wide">
                  Customer
               </span>
               <span v-else-if="authUser.user.type === 'agent'" class="mt-3 inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 uppercase tracking-wide">
                  {{ authUser.user.sub_type === 'sub_agent' ? 'Sub-Agent Partner' : 'Agent Partner' }}
               </span>
               <span v-else class="mt-3 inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800 uppercase tracking-wide">
                  Corporate Partner
               </span>
            </div>

            <!-- Tab Navigation Card -->
            <div class="bg-white rounded-2xl shadow-custom-symetric overflow-hidden border border-gray-100 p-4">
               <nav class="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-none">
                  <button type="button" @click="!showPasswordChangeAlert && (currentTab = 'personal')" :disabled="showPasswordChangeAlert" :class="[currentTab === 'personal' ? 'bg-theme-active text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900', showPasswordChangeAlert ? 'opacity-40 cursor-not-allowed' : '', 'flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap w-full text-left']">
                     <font-awesome-icon icon="fa-solid fa-user" class="w-5" /> Account Details
                  </button>
                  <button v-if="authUser.user.type === 'web'" type="button" @click="!showPasswordChangeAlert && (currentTab = 'bookings')" :disabled="showPasswordChangeAlert" :class="[currentTab === 'bookings' ? 'bg-theme-active text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900', showPasswordChangeAlert ? 'opacity-40 cursor-not-allowed' : '', 'flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap w-full text-left']">
                     <font-awesome-icon icon="fa-solid fa-list" class="w-5" /> My Bookings
                  </button>
                  <button v-if="authUser.user.type === 'web'" type="button" @click="!showPasswordChangeAlert && (currentTab = 'loyalty')" :disabled="showPasswordChangeAlert" :class="[currentTab === 'loyalty' ? 'bg-theme-active text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900', showPasswordChangeAlert ? 'opacity-40 cursor-not-allowed' : '', 'flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap w-full text-left']">
                     <font-awesome-icon icon="fa-solid fa-gift" class="w-5" /> Loyalty Rewards
                  </button>
                  <button type="button" @click="currentTab = 'password'" :class="[currentTab === 'password' ? 'bg-theme-active text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap w-full text-left']">
                     <font-awesome-icon icon="fa-solid fa-lock" class="w-5" /> Change Password
                  </button>
                  <div class="h-px bg-gray-100 my-2 hidden lg:block" />
                  <button type="button" @click="!showPasswordChangeAlert && handleLogout()" :disabled="showPasswordChangeAlert" :class="[showPasswordChangeAlert ? 'opacity-40 cursor-not-allowed' : '']" class="flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl text-red-600 hover:bg-red-50 transition-all duration-300 whitespace-nowrap w-full text-left">
                     <font-awesome-icon icon="fa-solid fa-right-from-bracket" class="w-5" /> Log Out
                  </button>
               </nav>
            </div>
         </div>

         <!-- Right Content Column: Render Active Tab -->
         <div class="lg:col-span-3 flex flex-col gap-6">
             <!-- 🚨 First-time login banner -->
             <div v-if="authUser.user.has_setup_password === 0" class="p-5 bg-amber-50 border border-amber-200/60 rounded-2xl flex gap-4 items-start shadow-sm transition-all duration-300">
                <div class="h-10 w-10 rounded-xl bg-amber-100/80 text-amber-800 flex items-center justify-center shrink-0">
                   <font-awesome-icon icon="fa-solid fa-circle-info" class="text-lg" />
                </div>
                <div>
                   <h4 class="font-bold text-amber-900 text-sm uppercase tracking-wider">Account Setup Required</h4>
                   <p class="text-xs text-amber-700/90 mt-1 leading-relaxed">
                      Please set your real email address and update your password to complete your account setup. This will unlock B2B dashboard features.
                   </p>
                </div>
             </div>

             <!-- Hidden File Input for Avatar -->
             <input class="hidden" ref="fileInput" type="file" @change="uploadMedia" accept="image/*">

            <!-- TAB 1: Account Details Form -->
            <div v-if="currentTab === 'personal'" class="bg-white rounded-2xl shadow-custom-symetric border border-gray-100 overflow-hidden transition-all duration-300">
               <div class="px-6 py-5 border-b border-gray-100">
                  <h2 class="text-xl font-bold text-gray-900">Account Details</h2>
                  <p class="mt-1 text-sm text-gray-500 font-regular">Review and update your personal contact details below.</p>
               </div>
               <form class="p-6 flex flex-col gap-6" @submit.prevent>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                        <label for="full-name" class="block text-sm font-bold text-gray-700">Full name *</label>
                        <div class="mt-2 relative rounded-md shadow-sm">
                           <input v-model="userAccount.personal.fullName" type="text" id="full-name" :class="errors.personal.fullName ? 'ring-red-500 border-red-500' : 'ring-gray-200 border-gray-200'" class="block w-full rounded-xl border py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" />
                        </div>
                        <p v-if="errors.personal.fullName" class="mt-2 text-sm text-red-600">This field is required</p>
                     </div>
                     <div>
                        <label for="email" class="block text-sm font-bold text-gray-700">Email Address</label>
                        <div class="mt-2">
                           <input 
                              v-model="userAccount.personal.email" 
                              type="text" 
                              :readonly="authUser.user.has_setup_password !== 0" 
                              :disabled="authUser.user.has_setup_password !== 0" 
                              id="email" 
                              :class="authUser.user.has_setup_password !== 0 ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900 focus:ring-theme-active'"
                              class="block w-full rounded-xl border border-gray-200 py-3 px-4 sm:text-sm font-regular outline-none transition-all duration-300" 
                           />
                        </div>
                     </div>
                     <div>
                        <label for="phone" class="block text-sm font-bold text-gray-700">Phone Number *</label>
                        <div class="mt-2 relative rounded-md shadow-sm">
                           <input v-model="userAccount.personal.phone" type="text" id="phone" :class="errors.personal.phone ? 'ring-red-500 border-red-500' : 'ring-gray-200 border-gray-200'" class="block w-full rounded-xl border py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" />
                        </div>
                        <p v-if="errors.personal.phone" class="mt-2 text-sm text-red-600">This field is required</p>
                     </div>
                     <div v-if="authUser.user.type === 'web' || authUser.user.type === 'agent'">
                        <label for="country" class="block text-sm font-bold text-gray-700">Country</label>
                        <div class="mt-2 relative rounded-md shadow-sm">
                           <input v-model="userAccount.personal.country" type="text" id="country" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter your country" />
                        </div>
                     </div>
                     <div v-if="authUser.user.type !== 'web'">
                        <label for="company-name" class="block text-sm font-bold text-gray-700">Company Name *</label>
                        <div class="mt-2 relative rounded-md shadow-sm">
                           <input v-model="userAccount.personal.company" type="text" id="company-name" :class="errors.personal.company ? 'ring-red-500 border-red-500' : 'ring-gray-200 border-gray-200'" class="block w-full rounded-xl border py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" />
                        </div>
                        <p v-if="errors.personal.company" class="mt-2 text-sm text-red-600">This field is required</p>
                     </div>
                  </div>

                  <!-- 📂 Agency Documents & Bank Details (Only visible for Parent Admin accounts) -->
                  <div v-if="authUser.user.type === 'agent' && userAccount.personal.isParent" class="border-t border-gray-100 pt-6 mt-4">
                     <h3 class="text-lg font-bold text-gray-900 mb-4">Agency Documents & Bank Details</h3>
                     
                     <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- CR Number -->
                        <div>
                           <label for="cr-number" class="block text-sm font-bold text-gray-700">CR Number</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.crNumber" type="text" id="cr-number" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter Commercial Registration Number" />
                           </div>
                        </div>

                        <!-- VAT Number -->
                        <div>
                           <label for="vat-number" class="block text-sm font-bold text-gray-700">VAT Number</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.vatNumber" type="text" id="vat-number" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter VAT Registration Number" />
                           </div>
                        </div>

                        <!-- Bank Name -->
                        <div>
                           <label for="bank-name" class="block text-sm font-bold text-gray-700">Bank Name</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.bankName" type="text" id="bank-name" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter Bank Name" />
                           </div>
                        </div>

                        <!-- Bank Account Name -->
                        <div>
                           <label for="bank-account-name" class="block text-sm font-bold text-gray-700">Bank Account Name</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.bankAccountName" type="text" id="bank-account-name" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter Account Name" />
                           </div>
                        </div>

                        <!-- Bank Account Number -->
                        <div>
                           <label for="bank-account-number" class="block text-sm font-bold text-gray-700">Bank Account Number</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.bankAccountNumber" type="text" id="bank-account-number" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter Account Number" />
                           </div>
                        </div>

                        <!-- IBAN -->
                        <div>
                           <label for="bank-iban" class="block text-sm font-bold text-gray-700">IBAN</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.bankIban" type="text" id="bank-iban" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter IBAN Number" />
                           </div>
                        </div>

                        <!-- SWIFT -->
                        <div>
                           <label for="bank-swift" class="block text-sm font-bold text-gray-700">SWIFT / BIC Code</label>
                           <div class="mt-2">
                              <input v-model="userAccount.personal.bankSwift" type="text" id="bank-swift" class="block w-full rounded-xl border border-gray-200 py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" placeholder="Enter SWIFT Code" />
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="flex items-center justify-between border-t border-gray-100 pt-6 mt-4">
                     <span class="text-xs text-gray-500 font-regular">* Required field</span>
                     <button @click="handlePersonalSubmit" type="button" :disabled="isLoading" class="rounded-xl bg-theme-active hover:bg-theme-active/90 text-white font-semibold text-sm px-6 py-3 shadow-md transition-all duration-300 flex items-center justify-center gap-2">
                        <font-awesome-icon v-if="isLoading" icon="fa-solid fa-spinner" class="animate-spin" />
                        Save Changes
                     </button>
                  </div>
               </form>
            </div>

            <!-- TAB 2: Bookings Table -->
            <div v-if="currentTab === 'bookings' && authUser.user.type === 'web'" class="bg-white rounded-2xl shadow-custom-symetric border border-gray-100 overflow-hidden transition-all duration-300">
               <div class="px-6 py-5 border-b border-gray-100">
                  <h2 class="text-xl font-bold text-gray-900">My Bookings</h2>
                  <p class="mt-1 text-sm text-gray-500 font-regular">View and manage all your transfer reservation requests and vouchers.</p>
               </div>
               <div class="p-6">
                  <BookingsTable subUserId="" />
               </div>
            </div>

            <!-- TAB 3: Change Password Form -->
            <div v-if="currentTab === 'password'" class="bg-white rounded-2xl shadow-custom-symetric border border-gray-100 overflow-hidden transition-all duration-300">
               <div class="px-6 py-5 border-b border-gray-100">
                  <h2 class="text-xl font-bold text-gray-900">Change Password</h2>
                  <p class="mt-1 text-sm text-gray-500 font-regular">Ensure your account stays secure by changing your password regularly.</p>
               </div>
               <form class="p-6 flex flex-col gap-6" @submit.prevent>
                  <div class="flex flex-col gap-6">
                     <div>
                        <label for="current-password" class="block text-sm font-bold text-gray-700">Current Password</label>
                        <div class="mt-2 relative rounded-md shadow-sm">
                           <input v-model="userAccount.password.current" type="password" id="current-password" :class="errors.password.current ? 'ring-red-500 border-red-500' : 'ring-gray-200 border-gray-200'" class="block w-full rounded-xl border py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" />
                        </div>
                        <p v-if="errors.password.current" class="mt-2 text-sm text-red-600">This field is required</p>
                     </div>
                     <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                           <label for="new-password" class="block text-sm font-bold text-gray-700">New Password</label>
                           <div class="mt-2 relative rounded-md shadow-sm">
                              <input v-model="userAccount.password.new" type="password" id="new-password" :class="errors.password.new ? 'ring-red-500 border-red-500' : 'ring-gray-200 border-gray-200'" class="block w-full rounded-xl border py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" />
                           </div>
                           <p v-if="errors.password.new" class="mt-2 text-sm text-red-600">{{ errors.password.new }}</p>
                        </div>
                        <div>
                           <label for="password-confirmation" class="block text-sm font-bold text-gray-700">Confirm Password</label>
                           <div class="mt-2 relative rounded-md shadow-sm">
                              <input v-model="userAccount.password.confirmNew" type="password" id="password-confirmation" :class="errors.password.confirmNew ? 'ring-red-500 border-red-500' : 'ring-gray-200 border-gray-200'" class="block w-full rounded-xl border py-3 px-4 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-theme-active focus:border-transparent outline-none transition-all duration-300 sm:text-sm" />
                           </div>
                           <p v-if="errors.password.confirmNew" class="mt-2 text-sm text-red-600">{{ errors.password.confirmNew }}</p>
                        </div>
                     </div>
                  </div>

               </form>
            </div>

            <!-- TAB 4: B2C Customer Loyalty Rewards -->
            <div v-if="currentTab === 'loyalty'" class="flex flex-col gap-6">
               <!-- Header -->
               <div class="bg-white rounded-2xl p-6 border border-gray-100 shadow-custom-symetric">
                  <h2 class="text-xl font-bold text-gray-900">Loyalty Program</h2>
                  <p class="text-sm text-gray-500 font-regular mt-1">Track your booking rewards points and request cashback payouts.</p>
               </div>

               <!-- Summary Target Banner -->
               <div v-if="customerLoyaltySummary" 
                    class="bg-gradient-to-r from-emerald-800 to-teal-950 rounded-3xl p-6 text-white shadow-lg border border-teal-700/30 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
                  <div class="absolute -right-16 -top-16 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
                  <div class="absolute -left-16 -bottom-16 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
                  
                  <div class="flex-grow w-full md:w-auto">
                     <div class="flex items-center gap-2 mb-1">
                        <font-awesome-icon icon="fa-solid fa-gift" class="text-xl text-emerald-400" />
                        <h3 class="text-lg font-extrabold tracking-wide uppercase">Loyalty Program Reward</h3>
                     </div>
                     <p class="text-xs text-emerald-300 font-semibold mb-3">
                        * Loyalty Program will be calculated from 01-07-2026
                     </p>
                     <p class="text-sm text-teal-100 font-semibold leading-relaxed max-w-2xl">
                        You have accumulated <span class="text-white font-extrabold text-lg px-1.5 py-0.5 rounded bg-white/10">{{ customerLoyaltySummary.points_earned }}</span> points 
                        (<span class="text-emerald-300 font-bold">${{ (Number(customerLoyaltySummary.points_earned) * (Number(customerLoyaltySummary.redeem_rate) || 0.1)).toFixed(2) }} USD</span>). 
                        Reach the target of <span class="font-extrabold">{{ customerLoyaltySummary.total_points }}</span> points 
                        (<span class="text-teal-300 font-bold">${{ (Number(customerLoyaltySummary.total_points) * (Number(customerLoyaltySummary.redeem_rate) || 0.1)).toFixed(2) }} USD</span>) to request a withdrawal!
                     </p>
                     <div class="mt-5 w-full bg-teal-950/50 rounded-full h-3 border border-teal-800/30">
                        <div class="bg-gradient-to-r from-emerald-400 to-teal-300 h-full rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]" 
                             :style="{ width: `${Math.min(100, ((Number(customerLoyaltySummary.points_earned) || 0) / Math.max(1, Number(customerLoyaltySummary.total_points) || 1)) * 100)}%` }"></div>
                     </div>
                     <div class="flex justify-between items-center mt-2 text-xs font-semibold text-teal-300">
                        <span>0 Points</span>
                        <span>{{ Math.min(100, Math.round(((Number(customerLoyaltySummary.points_earned) || 0) / Math.max(1, Number(customerLoyaltySummary.total_points) || 1)) * 100)) }}% Completed</span>
                        <span>{{ customerLoyaltySummary.total_points }} Points Target</span>
                     </div>
                  </div>
                  
                  <div class="flex-shrink-0 w-full md:w-auto text-right">
                     <button v-if="customerLoyaltySummary.pending_withdraw" 
                             disabled
                             class="w-full md:w-auto px-6 py-3.5 bg-emerald-900/50 border border-emerald-700/50 text-emerald-300/80 font-extrabold text-sm rounded-xl cursor-not-allowed uppercase tracking-wider">
                        Withdrawal Processing
                     </button>
                     <button v-else-if="Number(customerLoyaltySummary.points_earned) >= Number(customerLoyaltySummary.total_points)" 
                             @click="openB2CLoyaltyModal" 
                             class="w-full md:w-auto px-6 py-3.5 bg-white text-emerald-900 hover:bg-emerald-50 active:scale-[0.98] transition-all duration-200 font-extrabold text-sm rounded-xl shadow-md uppercase tracking-wider">
                        Withdraw Now
                     </button>
                     <button v-else
                             disabled
                             class="w-full md:w-auto px-6 py-3.5 bg-white/20 text-white/50 border border-white/10 font-extrabold text-sm rounded-xl cursor-not-allowed uppercase tracking-wider">
                        Withdraw Now
                     </button>
                  </div>
               </div>

               <!-- Point Ledger Table -->
               <div class="bg-white rounded-2xl shadow-custom-symetric border border-gray-100 overflow-hidden">
                  <div class="px-6 py-4 bg-gray-50 border-b border-gray-100">
                     <h3 class="font-bold text-gray-900 text-sm">Loyalty Points History</h3>
                  </div>
                  <div class="overflow-x-auto">
                     <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50/50">
                           <tr>
                              <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                              <th scope="col" class="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking ID</th>
                              <th scope="col" class="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Amount</th>
                              <th scope="col" class="px-3 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pr-6">Points</th>
                           </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white">
                           <tr v-for="item in loyaltyLogsList" :key="item.id" class="hover:bg-gray-50/50 transition-colors duration-150">
                              <td class="whitespace-nowrap py-4 pl-6 pr-3 text-sm text-gray-900">
                                 {{ item.created_at.split('T')[0] }}
                                 <span v-if="Number(item.points) < 0" class="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 uppercase">Payout</span>
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-semibold">
                                 {{ item.booking_number }}
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                 {{ item.amount_usd ? `$${Number(item.amount_usd).toFixed(2)}` : '-' }}
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-right pr-6 font-bold" :class="Number(item.points) >= 0 ? 'text-emerald-600' : 'text-rose-600'">
                                 {{ Number(item.points) >= 0 ? `+${Number(item.points).toFixed(2)}` : Number(item.points).toFixed(2) }}
                              </td>
                           </tr>
                           <tr v-if="loyaltyLogsList.length === 0">
                              <td colspan="4" class="text-center py-10 text-sm text-gray-500 font-medium">
                                 No loyalty point transactions found.
                              </td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
               </div>
            </div>

            <!-- B2C Withdrawal Dialog Modal -->
            <div v-if="showLoyaltyRedeemModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
               <div class="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-md w-full p-8 flex flex-col relative overflow-hidden">
                  <h3 class="text-xl font-bold text-gray-900 mb-2">Request Loyalty Points Payout</h3>
                  <p class="text-xs text-gray-500 font-medium mb-6">Enter bank details to receive the cash value.</p>
                  
                  <div class="flex flex-col gap-4">
                     <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bank Name *</label>
                        <input v-model="loyaltyRedeemForm.bankName.value" type="text" class="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none focus:border-theme-active focus:bg-white" />
                        <span v-if="loyaltyRedeemForm.bankName.error" class="text-xs text-rose-600 font-semibold">Required</span>
                     </div>
                     <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account Title Name *</label>
                        <input v-model="loyaltyRedeemForm.accountName.value" type="text" class="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none focus:border-theme-active focus:bg-white" />
                        <span v-if="loyaltyRedeemForm.accountName.error" class="text-xs text-rose-600 font-semibold">Required</span>
                     </div>
                     <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account Number or IBAN *</label>
                        <input v-model="loyaltyRedeemForm.accountNumber.value" type="text" class="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none focus:border-theme-active focus:bg-white" />
                        <span v-if="loyaltyRedeemForm.accountNumber.error" class="text-xs text-rose-600 font-semibold">Required</span>
                     </div>
                     <div class="flex flex-col gap-1.5">
                        <label class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bank Country *</label>
                        <input v-model="loyaltyRedeemForm.countryName.value" type="text" class="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none focus:border-theme-active focus:bg-white" />
                        <span v-if="loyaltyRedeemForm.countryName.error" class="text-xs text-rose-600 font-semibold">Required</span>
                     </div>
                     
                     <div class="flex flex-col gap-1.5 mt-2">
                        <label for="b2c-withdraw-points" class="text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">Points to Withdraw *</label>
                        <div class="relative rounded-xl border border-gray-200 flex items-center bg-gray-50 focus-within:bg-white focus-within:border-theme-active transition-all duration-200">
                           <input v-model="b2cPointsToWithdraw" 
                                  type="number" 
                                  id="b2c-withdraw-points" 
                                  :max="customerLoyaltySummary?.points_earned"
                                  placeholder="Enter points count" 
                                  class="w-full bg-transparent outline-none px-4 py-2.5 font-bold text-gray-800 text-sm" />
                           <span class="pr-4 text-xs font-bold text-emerald-600 whitespace-nowrap">
                              ${{ (Number(b2cPointsToWithdraw || 0) * (Number(customerLoyaltySummary?.redeem_rate) || 0.1)).toFixed(2) }} USD
                           </span>
                        </div>
                        <span v-if="b2cPointsError" class="text-xs text-rose-600 font-semibold mt-1 text-left">{{ b2cPointsError }}</span>
                        <p class="text-[10px] text-gray-400 mt-1 font-medium text-left">Available balance: <strong>{{ customerLoyaltySummary?.points_earned }}</strong></p>
                     </div>
                     
                     <p v-if="loyaltyRedeemForm.error" class="text-xs text-rose-600 font-semibold mt-2 text-left">{{ loyaltyRedeemForm.error }}</p>
                     
                     <div class="flex items-center justify-end gap-3 mt-6">
                        <button type="button" @click="showLoyaltyRedeemModal = false" class="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-xs font-bold text-gray-600 uppercase transition-all">Cancel</button>
                        <button type="button" @click="handleLoyaltySubmit" :disabled="isLoyaltyBusy" class="px-5 py-2.5 bg-theme-active text-white rounded-xl text-xs font-bold uppercase hover:bg-theme-active/90 disabled:opacity-50 transition-all flex items-center gap-1.5">
                           <font-awesome-icon v-if="isLoyaltyBusy" icon="fa-solid fa-spinner" class="animate-spin" />
                           Submit
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <!-- 🚨 FORCED PASSWORD CHANGE MODAL -->
      <div v-if="showPasswordChangeAlert" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
         <div class="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-md w-full p-8 flex flex-col items-center text-center relative overflow-hidden transition-all duration-300 transform scale-100">
            <div class="h-16 w-16 bg-red-50 rounded-full flex items-center justify-center text-red-500 mb-6 ring-8 ring-red-50">
               <font-awesome-icon icon="fa-solid fa-lock" class="text-3xl" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 tracking-tight">Security Alert!</h3>
            <p class="mt-3 text-sm text-gray-500 leading-relaxed font-regular">
               You are logged in with a temporary system password. For your security, you must update your password before you can view your bookings or manage your profile.
            </p>
            <button type="button" @click="showPasswordChangeAlert = false" class="mt-8 w-full rounded-xl bg-theme-active hover:bg-theme-active/90 text-white font-bold text-sm py-4 shadow-lg transition-all duration-300 tracking-wide uppercase">
               Change Password Now
            </button>
         </div>
      </div>
   </div>

   <Notification :show="notif.show" :type="notif.type" :message="notif.message" :messageDetails="notif.messageDetails" @closed="notifClosed"/>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import Agent from '@/composables/admin/agent'
import Corporate from '@/composables/admin/corporate'
import User from '@/composables/User'
import BookingsTable from '@/components/account/BookingsTable.vue'
import { handleApiError, isImageFile, isLoggedIn, resetErrors, truncateText, updateAuthUser, logout } from '@/utils/common'
import { NotificationType } from '@/types'
import Notification from '@/widgets/Notification.vue'

const props = defineProps(['userType'])
const authUser = isLoggedIn()
const router = useRouter()
const currentTab = ref<string>('personal')
const showPasswordChangeAlert = ref<boolean>(false)

const { agentProfile, agentProfileSave } = Agent()
const { corporateProfile, corporateProfileSave } = Corporate()
const { userProfile, userProfileSave, customerLoyaltyLogs, customerRequestRedeemLoyalty } = User()

const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})
const isLoading = ref<boolean>(true)
const fileInput = ref<HTMLInputElement | null>(null)

const userAccount = ref<any>({
   personal: {
      fullName: '',
      email: '',
      photo: null,
      photoName: '',
      commission: 0,
      phone: '',
      company: '',
      country: '',
      crNumber: '',
      vatNumber: '',
      bankName: '',
      bankAccountName: '',
      bankAccountNumber: '',
      bankIban: '',
      bankSwift: '',
      isParent: false
   },
   password: {
      current: '',
      new: '',
      confirmNew: ''
   }
})

const errors = ref<any>({
   personal: {
      fullName: null,
      commission: null,
      phone: null,
      company: null
   },
   password: {
      current: null,
      new: null,
      confirmNew: null
   }
})

onMounted(async () => {
   if (authUser && authUser.user.type == 'agent') {
      await fetchAgentProfile()
      if (authUser.user.has_setup_password === 0) {
         currentTab.value = 'password'
         showPasswordChangeAlert.value = true
      }
   } else if (authUser && authUser.user.type == 'corporate'){
      await fetchCorporateProfile()
   } else if (authUser && authUser.user.type == 'web'){
      await fetchUserProfile()
      document.title = 'My Profile - Vehitra World'
      if (authUser.user.require_password_change) {
         currentTab.value = 'password'
         showPasswordChangeAlert.value = true
      }
      await fetchCustomerLoyalty()
   }
})

const loyaltyLogsList = ref<any[]>([])
const customerLoyaltySummary = ref<any>(null)
const showLoyaltyRedeemModal = ref<boolean>(false)
const isLoyaltyBusy = ref<boolean>(false)
const loyaltyRedeemForm = ref<any>({
   bankName: { value: '', error: false, errorMessage: '' },
   accountName: { value: '', error: false, errorMessage: '' },
   accountNumber: { value: '', error: false, errorMessage: '' },
   countryName: { value: '', error: false, errorMessage: '' },
   error: ''
})

const fetchCustomerLoyalty = async () => {
   const response = await customerLoyaltyLogs()
   if (response.status === 200) {
      loyaltyLogsList.value = response.data.logs || []
      customerLoyaltySummary.value = response.data.loyalty_summary || null
   }
}

watch(currentTab, async (newTab) => {
   if (newTab === 'loyalty') {
      await fetchCustomerLoyalty()
   }
})

const b2cPointsToWithdraw = ref<number | null>(null)
const b2cPointsError = ref<string>('')

const openB2CLoyaltyModal = () => {
   b2cPointsToWithdraw.value = customerLoyaltySummary.value ? Math.floor(Number(customerLoyaltySummary.value.points_earned)) : 0
   b2cPointsError.value = ''
   showLoyaltyRedeemModal.value = true
}

const handleLoyaltySubmit = async () => {
   let isValid = true
   loyaltyRedeemForm.value.bankName.error = false
   loyaltyRedeemForm.value.accountName.error = false
   loyaltyRedeemForm.value.accountNumber.error = false
   loyaltyRedeemForm.value.countryName.error = false
   loyaltyRedeemForm.value.error = ''
   b2cPointsError.value = ''

   if (loyaltyRedeemForm.value.bankName.value === '') {
      loyaltyRedeemForm.value.bankName.error = true
      isValid = false
   }
   if (loyaltyRedeemForm.value.accountName.value === '') {
      loyaltyRedeemForm.value.accountName.error = true
      isValid = false
   }
   if (loyaltyRedeemForm.value.accountNumber.value === '') {
      loyaltyRedeemForm.value.accountNumber.error = true
      isValid = false
   }
   if (loyaltyRedeemForm.value.countryName.value === '') {
      loyaltyRedeemForm.value.countryName.error = true
      isValid = false
   }
   if (!b2cPointsToWithdraw.value || b2cPointsToWithdraw.value <= 0) {
      b2cPointsError.value = 'Please enter a valid points amount'
      isValid = false
   } else if (customerLoyaltySummary.value && b2cPointsToWithdraw.value > Number(customerLoyaltySummary.value.points_earned)) {
      b2cPointsError.value = 'Cannot exceed your accumulated points'
      isValid = false
   }

   if (!isValid) return

   isLoyaltyBusy.value = true
   const response = await customerRequestRedeemLoyalty(
      loyaltyRedeemForm.value.bankName.value,
      loyaltyRedeemForm.value.accountName.value,
      loyaltyRedeemForm.value.accountNumber.value,
      loyaltyRedeemForm.value.countryName.value,
      b2cPointsToWithdraw.value || 0
   )
   isLoyaltyBusy.value = false

   if (response.status === 200) {
      showLoyaltyRedeemModal.value = false
      notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Withdrawal request submitted successfully' }
      setTimeout(() => { notifClosed() }, 5000)
      // reset form
      loyaltyRedeemForm.value.bankName.value = ''
      loyaltyRedeemForm.value.accountName.value = ''
      loyaltyRedeemForm.value.accountNumber.value = ''
      loyaltyRedeemForm.value.countryName.value = ''
      
      await fetchCustomerLoyalty()
   } else {
      loyaltyRedeemForm.value.error = handleApiError(response)
   }
}

const handleLogout = () => {
   logout(router)
}

const fetchAgentProfile = async () => {
   isLoading.value = true
   const response = await agentProfile()
   isLoading.value = false

   if (response.status == 200) {
      userAccount.value.personal = {
         fullName: response.data.data.name,
         email: response.data.data.email,
         photoName: response.data.data.photo,
         commission: response.data.data.commission,
         phone: response.data.data.phone,
         company: response.data.data.company,
         crNumber: response.data.data.cr_number || '',
         vatNumber: response.data.data.vat_number || '',
         bankName: response.data.data.bank_name || '',
         bankAccountName: response.data.data.bank_account_name || '',
         bankAccountNumber: response.data.data.bank_account_number || '',
         bankIban: response.data.data.bank_iban || '',
         bankSwift: response.data.data.bank_swift || '',
         country: response.data.data.country || '',
         isParent: response.data.data.is_parent
      }
   } else {
      const error = handleApiError(response)
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
      setTimeout(() => { notifClosed() }, 4000)
   }
}

const fetchCorporateProfile = async () => {
   isLoading.value = true
   const response = await corporateProfile()
   isLoading.value = false

   if (response.status == 200) {
      userAccount.value.personal = {
         fullName: response.data.data.name,
         email: response.data.data.email,
         photoName: response.data.data.photo,
         commission: response.data.data.commission,
         phone: response.data.data.phone,
         company: response.data.data.company
      }
   } else {
      const error = handleApiError(response)
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
      setTimeout(() => { notifClosed() }, 4000)
   }
}

const fetchUserProfile = async () => {
   isLoading.value = true
   const response = await userProfile()
   isLoading.value = false

   if (response.status == 200) {
      userAccount.value.personal = {
         fullName: response.data.data.name,
         email: response.data.data.email,
         photoName: response.data.data.photo,
         commission: response.data.data.commission,
         phone: response.data.data.phone,
         company: response.data.data.company,
         country: response.data.data.country || ''
      }
   } else {
      const error = handleApiError(response)
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
      setTimeout(() => { notifClosed() }, 4000)
   }
}

const handlePersonalSubmit = async () => {
   if (!isLoading.value) {
      resetErrors(errors.value)
      const isValid = validateForm('personal')
   
      if (isValid) {
         isLoading.value = true
         let response : any

         if (authUser && authUser.user.type == 'agent') {
            response = await agentProfileSave(userAccount.value.personal, 'personal')
         } else if (authUser && authUser.user.type == 'corporate') {
            response = await corporateProfileSave(userAccount.value.personal, 'personal')
         } else if (authUser && authUser.user.type == 'web') {
            response = await userProfileSave(userAccount.value.personal, 'personal')
         }

         isLoading.value = false
         if (response.status == 200) {
            notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Profile updated'}
            setTimeout(() => { notifClosed() }, 4000)
            
            if (authUser && authUser.user.type == 'agent') {
               await fetchAgentProfile()
            } else if (authUser && authUser.user.type == 'corporate') {
               await fetchCorporateProfile()
            } else if (authUser && authUser.user.type == 'web') {
               await fetchUserProfile()
            }

            updateAuthUser(response.data.user)
         } else {
            const error = handleApiError(response)
            notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
            setTimeout(() => { notifClosed() }, 4000)
         } 
      }
   }
}

const handlePasswordSubmit = async () => {
   if (!isLoading.value) {
      resetErrors(errors.value)
      const isValid = validateForm('password')
   
      if (isValid) {
         isLoading.value = true
         let response : any

         if (authUser && authUser.user.type == 'agent') {
            response = await agentProfileSave(userAccount.value.password, 'password')
         } else if (authUser && authUser.user.type == 'corporate') {
            response = await corporateProfileSave(userAccount.value.password, 'password')
         } else if (authUser && authUser.user.type == 'web') {
            response = await userProfileSave(userAccount.value.password, 'password')
         }

         isLoading.value = false
         if (response.status == 200) {
            userAccount.value.password = {
               current: '',
               new: '',
               confirmNew: ''
            }
            notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Password updated'}
            setTimeout(() => { notifClosed() }, 4000)
            showPasswordChangeAlert.value = false
         } else {
            const error = handleApiError(response)
            notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
            setTimeout(() => { notifClosed() }, 4000)
         }
      }
   }
}

const notifClosed = () : void => {
   notif.value.show = false
}

const validateForm = (type: string) : boolean => {
   let response = true

   if (type == 'personal') {
      if (authUser && authUser.user.type === 'web') {
         if (userAccount.value.personal.fullName == '') {
            errors.value.personal.fullName = true
            response = false
         }
         if (userAccount.value.personal.phone == '') {
            errors.value.personal.phone = true
            response = false
         }
      }
      if (authUser && authUser.user.type !== 'web') {
         if (userAccount.value.personal.commission < 0 || userAccount.value.personal.commission > 50) {
            errors.value.personal.commission = 'Invalid value. It must be between 1 and 50'
            response = false
         }
      }
   } else if (type == 'password'){
      if (userAccount.value.password.current == '') {
         errors.value.password.current = true
         response = false
      }

      if (userAccount.value.password.new == '') {
         errors.value.password.new = 'This field is required'
         response = false
      } else if (userAccount.value.password.new.length < 6) {
         errors.value.password.new = 'Password must be at least 6 characters long'
         response = false
      }

      if (userAccount.value.password.confirmNew == '') {
         errors.value.password.confirmNew = 'This field is required'
         response = false
      } else if (userAccount.value.password.new != '' && userAccount.value.password.new != userAccount.value.password.confirmNew){
         errors.value.password.confirmNew = 'Confirm password must be the same as new password'
         response = false
      }
   }

   return response
}

const handleChooseAvatar = () => {
   fileInput.value?.click()
}

const uploadMedia = (e: Event) => {
   userAccount.value.personal.photo = null
   userAccount.value.personal.photoName = ''
   const inputElement = e.target as HTMLInputElement

   if (!inputElement.files || inputElement.files.length === 0) {
      return;
   }

   const file = inputElement.files[0]
   const reader = new FileReader()

   if (!isImageFile(file)) {
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'File must be an image of type JPG or PNG'}
   } else if (file.size > 3145728) {
      notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Image must be 3MB or less'}
   }

   userAccount.value.personal.photoName = file.name

   reader.onloadend = () => {
      userAccount.value.personal.photo = reader.result as string
   }

   reader.readAsDataURL(file)
}
</script>